/**
 * 공용 UI 조각.
 *
 * 컴포넌트 라이브러리를 넣지 않았다. 콘솔이 쓰는 것은 표·배지·모달 정도이고,
 * 사이니지 콘솔은 **화면 상태를 한눈에 보는 것**이 본질이라 밀도와 색이 중요하다.
 * 남의 디자인 시스템을 억지로 맞추는 것보다 여기 몇 개를 직접 두는 편이 낫다.
 */
import { HelpTip } from './HelpTip.js';
import { copyText } from '../clipboard.js';
import { createPortal } from 'react-dom';
import {
  useEffect,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { Check, ChevronDown, Copy } from 'lucide-react';

export function Badge({
  tone = 'neutral',
  title,
  children,
}: {
  tone?: 'neutral' | 'ok' | 'warn' | 'danger' | 'info' | 'brand';
  /** 마우스를 올리면 보이는 설명(예: 오류 보고 배지의 오류 글). */
  title?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <span className={`badge badge-${tone}`} title={title}>
      {children}
    </span>
  );
}

/** 온라인 여부 점. 목록에서 상태를 훑을 때 색만 보고 판단하게 한다. */
/**
 * 목록 줄의 이름 — **누르면 그 줄을 고치는 창이 열린다**(2026-10-22 사용자 지시 "어떤 메뉴의 리스트는 타이틀을 누르면
 * 수정창이 열리고 어떤건 안열리고 모두 제각각인데 모두 열리게").
 *
 * 표에서 이름은 가장 먼저 눈이 닿고 가장 큰 과녁이다. 오른쪽 끝의 [편집] 만 동작하면, 이름을 눌러 본 사람은 아무 일도
 * 일어나지 않는 것을 보고 그 표가 클릭 가능한지 다시 가늠하게 된다(플레이리스트 목록이 먼저 이렇게 했다).
 *
 * 고칠 수 없는 사람(`onOpen` 없음)에게는 글자로만 둔다 — 편집기는 [저장] 을 품고 있어, 열어 주면 조회 역할도 저장을
 * 눌러 보고 나서야 403 을 만난다(출하 검사 2.4). 줄 전체가 다른 일(고르기·펼치기)을 하는 표에서도 이름만은 편집을 연다 —
 * 줄로 번지지 않게 누르기를 여기서 멈춘다.
 */
export function ItemTitle({
  onOpen,
  label,
  className = 'cell-strong',
  children,
}: {
  /** 고치는 창을 연다 — 고칠 수 없으면 주지 않는다. */
  onOpen?: () => void;
  /** 화면 읽기 프로그램에 읽힐 동작 이름(예: "로비 편집"). 없으면 글자 그대로. */
  label?: string;
  className?: string;
  children: ReactNode;
}): ReactNode {
  if (!onOpen) return <span className={className}>{children}</span>;
  return (
    <button
      type="button"
      className={`${className} item-title`}
      aria-label={label}
      onClick={(event) => {
        event.stopPropagation();
        onOpen();
      }}
    >
      {children}
    </button>
  );
}

export function StatusDot({ online }: { online: boolean }): ReactNode {
  return (
    <span
      className={`dot ${online ? 'dot-ok' : 'dot-off'}`}
      title={online ? '온라인' : '오프라인'}
      aria-label={online ? '온라인' : '오프라인'}
    />
  );
}

/**
 * 구역 하나.
 *
 * `collapsible` 을 주면 머리를 눌러 접을 수 있다. **접히는 것은 본문뿐이고 머리는
 * 남는다** — 제목과 부제(개수·상태 한 줄)가 접힌 상태에서도 보여야, 펼치지 않고도
 * "여기에 무엇이 몇 개 있나" 를 알 수 있다. 접었다고 알림이 사라지면 접기가 아니라
 * 숨기기가 된다.
 *
 * 접기 손잡이는 `<details>` 가 아니라 머리 안쪽 버튼이다 — 오른쪽 `actions` 에 버튼이
 * 오는 자리라, `summary` 로 감싸면 버튼 안에 버튼이 들어간다.
 */
export function Panel({
  title,
  subtitle,
  help,
  actions,
  collapsible = false,
  defaultOpen = true,
  className,
  children,
}: {
  title: string;
  /** 제목만으로 부족할 때 한 줄. 없으면 아예 자리를 차지하지 않는다. */
  subtitle?: string;
  /** 제목 옆 물음표의 설명(`HelpTip`). 처음 한 번 읽으면 되는 말은 부제 대신 여기에. */
  help?: ReactNode;
  actions?: ReactNode;
  /** 머리를 눌러 본문을 접을 수 있게 한다. */
  collapsible?: boolean;
  /** 접을 수 있을 때 처음 상태 — 펼친 채가 기본이다(2026-10-15 사용자 지시). */
  defaultOpen?: boolean;
  /** 격자 안의 자리(칸·줄)처럼 놓이는 곳이 정하는 것. */
  className?: string;
  children: ReactNode;
}): ReactNode {
  const [open, setOpen] = useState(defaultOpen);
  const shown = !collapsible || open;
  /*
   * 물음표는 제목 바로 뒤에 둔다. 접히는 패널이면 제목이 접기 단추 안에 있어 그 안에 단추를 넣을 수
   * 없으므로(단추 안의 단추), 접기 단추 바로 바깥에 둔다.
   */
  const tip = help ? <HelpTip text={help} label={`${title} 도움말`} /> : null;
  const head = (
    <div>
      <h2>
        {title}
        {collapsible ? null : tip}
      </h2>
      {subtitle ? <p className="panel-sub">{subtitle}</p> : null}
    </div>
  );

  return (
    <section className={className ? `panel ${className}` : 'panel'}>
      <header className={`panel-head ${shown ? '' : 'is-closed'}`}>
        {collapsible ? (
          <button
            type="button"
            className="panel-toggle"
            aria-expanded={open}
            onClick={() => setOpen((value) => !value)}
          >
            <ChevronDown
              className={`panel-chevron ${open ? '' : 'is-closed'}`}
              strokeWidth={2}
              aria-hidden="true"
            />
            {head}
          </button>
        ) : (
          head
        )}
        {collapsible && tip ? <span className="panel-help">{tip}</span> : null}
        {actions ? <div className="panel-actions">{actions}</div> : null}
      </header>
      {shown ? <div className="panel-body">{children}</div> : null}
    </section>
  );
}

export function ErrorNote({ message, onDismiss }: { message: string; onDismiss?: () => void }): ReactNode {
  return (
    <div className="note note-error" role="alert">
      <span>{message}</span>
      {onDismiss ? (
        <button type="button" className="link" onClick={onDismiss}>
          닫기
        </button>
      ) : null}
    </div>
  );
}

/**
 * 빈 상태.
 *
 * "없음"만 보여주면 사용자는 다음에 뭘 해야 할지 모른다. 무엇이 없고 어떻게 만드는지를
 * 같이 적는다 — 특히 사이니지는 처음 설정 단계가 길어 여기서 막히는 일이 많다.
 */
export function Empty({ title, hint, action }: { title: string; hint: string; action?: ReactNode }): ReactNode {
  return (
    <div className="empty">
      <p className="empty-title">{title}</p>
      <p className="empty-hint">{hint}</p>
      {action}
    </div>
  );
}

export function Loading(): ReactNode {
  return <div className="loading">불러오는 중…</div>;
}

/**
 * 붙여 넣을 명령 한 덩어리.
 *
 * 현장에서 이걸 손으로 옮겨 적으면 반드시 오타가 난다 — 설치 토큰이나 서명 URL 처럼
 * 긴 문자열이 섞여 있다. 그래서 복사 버튼이 주인공이고, 클립보드가 막힌 환경에서도
 * 명령 자체는 화면에 그대로 보인다(`select-all` 로 한 번에 잡힌다).
 */
export function CommandBox({ command }: { command: string }): ReactNode {
  const [copied, setCopied] = useState(false);

  return (
    <div className="flex items-start gap-2 rounded-lg border border-slate-700 bg-slate-900 p-3">
      {/* 줄바꿈을 살린다 — adb 처럼 여러 줄인 명령이 한 줄로 뭉개지면 붙여 넣어도 안 된다. */}
      <code className="flex-1 select-all whitespace-pre-wrap break-all font-mono text-[12.5px] leading-relaxed text-slate-100">
        {command}
      </code>
      <button
        type="button"
        className="btn btn-tiny shrink-0"
        onClick={(event) => {
          // 평문 http 의 사내 주소에서도 복사된다(copyText). 못 넣어도 명령은 화면에 보인다.
          void copyText(command, event.currentTarget).then(setCopied);
        }}
      >
        {copied ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : <Copy className="h-3.5 w-3.5" strokeWidth={2} />}
        {copied ? '복사됨' : '복사'}
      </button>
    </div>
  );
}

/**
 * 배경 클릭으로 모달을 닫을 것인가.
 *
 * **누른 곳과 뗀 곳이 둘 다 배경이어야 한다.** 한쪽만 보면 모달 안에서 누르고
 * 배경에서 뗐을 때도 닫히는데, 그 순간 입력하던 내용이 통째로 사라진다.
 * 입력칸의 글자를 드래그로 선택하다 손이 밖으로 나가거나, 편성 항목을 끌어 옮기다
 * 모달 밖에서 놓는 일은 드물지 않다.
 *
 * 순수 함수로 둔 이유는 이 판단이 **잘못되면 사용자의 작업이 날아가기** 때문이다.
 * 이벤트 배선은 눈으로 확인해야 하지만 규칙은 고정할 수 있다.
 */
export function shouldCloseOnBackdrop(pressedOnBackdrop: boolean, releasedOnBackdrop: boolean): boolean {
  return pressedOnBackdrop && releasedOnBackdrop;
}

/** 창 안에서 Tab 으로 오갈 수 있는 것. */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) => element.offsetParent !== null || element === document.activeElement,
  );
}

/**
 * 창을 열 때 처음 초점을 줄 곳 — 이미 창 안에 있으면 그대로, 아니면 `data-autofocus` 를 단 것, 그다음
 * 첫 입력칸, 입력칸이 없으면 창 자체. 닫기 단추(✕)에 주지 않는다 — Enter 한 번에 창이 닫힌다.
 *
 * React 의 `autoFocus` 를 쓰지 않는 이유: 키 처리 중에 열린 창(편집 창의 Esc 가 띄운 확인 창)에서는
 * 그 초점이 남지 않았다. 표시를 달아 두고 여기서 한 번에 고른다.
 */
export function initialFocus(dialog: HTMLElement, active: Element | null): HTMLElement {
  if (active instanceof HTMLElement && dialog.contains(active)) return active;
  const marked = dialog.querySelector<HTMLElement>('[data-autofocus]');
  if (marked) return marked;
  const body = dialog.querySelector<HTMLElement>('.modal-body') ?? dialog;
  const field = focusables(body).find((element) => /^(INPUT|SELECT|TEXTAREA)$/.test(element.tagName));
  return field ?? dialog;
}

/** Tab 이 창 끝에서 처음으로, Shift+Tab 이 처음에서 끝으로 돈다 — 창 뒤의 화면으로 새지 않는다. */
export function nextTrapTarget(
  items: readonly HTMLElement[],
  current: Element | null,
  backwards: boolean,
): HTMLElement | null {
  if (items.length === 0) return null;
  const first = items[0]!;
  const last = items[items.length - 1]!;
  const index = current ? items.indexOf(current as HTMLElement) : -1;
  if (index === -1) return backwards ? last : first;
  if (backwards && index === 0) return last;
  if (!backwards && index === items.length - 1) return first;
  return null;
}

/** 한글 입력 중의 Esc 는 조합을 끝내는 키다 — 창을 닫는 키로 읽지 않는다. */
export function isComposingKey(event: { isComposing?: boolean; keyCode?: number }): boolean {
  return event.isComposing === true || event.keyCode === 229;
}

export function Modal({
  title,
  onClose,
  wide = false,
  children,
}: {
  title: string;
  onClose: () => void;
  /** 폼과 미리보기를 나란히 두는 편집기처럼 넓은 화면이 필요할 때. */
  wide?: boolean;
  children: ReactNode;
}): ReactNode {
  /*
   * **body 로 포털한다.**
   *
   * 모달은 화면 안(`main`) 에서 렌더되는데, 그 자리에서는 `z-index` 를 아무리
   * 올려도 사이드바(`fixed z-40`) 위로 올라가지 못했다. 조상 어딘가가 쌓임
   * 맥락을 만들어 모달의 z 가 그 안에서만 유효했기 때문이다. 실제로 편집기
   * 모달을 띄운 채로 사이드바 메뉴가 눌렸다.
   *
   * 원인을 조상에서 찾아 없애는 대신 body 로 옮긴다. 어떤 화면에서 열리든
   * 결과가 같다는 것이 이 방식의 값어치다.
   */
  /*
   * **누른 곳과 뗀 곳이 둘 다 배경일 때만 닫는다.**
   *
   * `click` 하나로 판단하면 모달 안에서 누르고 배경에서 뗐을 때도 닫힌다 —
   * `click` 은 누른 곳과 뗀 곳의 공통 조상에서 발생하는데, 그게 배경이기 때문이다.
   * 실제로 이런 일이 자주 일어난다: 입력칸의 글자를 드래그로 선택하다 손이 밖으로
   * 나가거나, 편성 항목을 끌어 옮기다 모달 밖에서 놓는 경우다. 그때 **입력하던
   * 내용이 통째로 사라진다.**
   *
   * `click` 대신 pointer 로 양쪽을 직접 본다. 공통 조상 규칙을 거치지 않아
   * 판단이 단순해지고, 마우스·터치·펜이 같은 경로를 탄다.
   */
  const pressedOnBackdrop = useRef(false);
  const dialog = useRef<HTMLDivElement>(null);
  const latestClose = useRef(onClose);
  latestClose.current = onClose;
  /*
   * 닫을 때 돌려줄 초점 — **그리기 전에** 잡는다. 창 안의 단추가 `autoFocus` 면 효과(effect)가 돌 때는
   * 이미 초점이 창 안에 들어와 있어, 그때 잡으면 돌려줄 자리를 잃는다.
   */
  const [returnTo] = useState(() => (document.activeElement instanceof HTMLElement ? document.activeElement : null));

  /*
   * 열 때 초점을 창 안으로 옮기고, 닫으면 연 자리(누른 단추)로 돌려준다. 돌려주지 않으면 키보드로
   * 일하던 사람의 초점이 문서 맨 앞으로 튀어, 목록의 제자리를 다시 찾아 Tab 을 수십 번 누른다.
   */
  useEffect(() => {
    if (dialog.current) initialFocus(dialog.current, document.activeElement).focus({ preventScroll: true });
    return () => {
      if (returnTo?.isConnected) returnTo.focus({ preventScroll: true });
    };
  }, [returnTo]);

  /*
   * Esc 로 닫고, Tab 은 창 안에서 돈다.
   *
   * 창 요소에 단다(문서에 달지 않는다). 창 안의 창(포털)도 React 에서는 바깥 창의 자식이라
   * 키가 바깥 창까지 올라온다 — 안쪽 창이 처리하고 멈추게 해, Esc 한 번에 한 겹만 닫힌다.
   */
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (event.defaultPrevented) return;
    if (event.key === 'Escape' && !isComposingKey(event.nativeEvent)) {
      event.stopPropagation();
      event.preventDefault();
      latestClose.current();
      return;
    }
    if (event.key === 'Tab' && dialog.current) {
      const target = nextTrapTarget(focusables(dialog.current), document.activeElement, event.shiftKey);
      event.stopPropagation();
      if (target) {
        event.preventDefault();
        target.focus();
      }
    }
  };

  return createPortal(
    <div
      className="modal-backdrop"
      role="presentation"
      onPointerDown={(event) => {
        pressedOnBackdrop.current = event.target === event.currentTarget;
      }}
      onPointerUp={(event) => {
        const releasedOnBackdrop = event.target === event.currentTarget;
        if (shouldCloseOnBackdrop(pressedOnBackdrop.current, releasedOnBackdrop)) onClose();
        pressedOnBackdrop.current = false;
      }}
    >
      <div
        ref={dialog}
        className={`modal ${wide ? 'modal-wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <header className="modal-head">
          <h3>{title}</h3>
          <button type="button" className="link" onClick={onClose} aria-label="닫기">
            ✕
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * 누르면 열리는 표 줄 — 마우스뿐 아니라 키보드로도(Tab 으로 와서 Enter·Space).
 *
 * 줄에 `onClick` 만 달면 초점이 가지 않아, 키보드로 일하는 사람은 상세를 열 길이 없었다. 줄 안의 체크칸·
 * 단추에서 누른 Enter 는 그것들의 일이라 건드리지 않는다(줄 자체에 초점이 있을 때만 연다).
 */
export function rowAction(open: () => void): Pick<HTMLAttributes<HTMLTableRowElement>, 'tabIndex' | 'onClick' | 'onKeyDown'> {
  return {
    tabIndex: 0,
    onClick: open,
    onKeyDown: (event) => {
      if (event.target !== event.currentTarget) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        open();
      }
    },
  };
}

/** 정확한 시각 — 이 브라우저의 시간대로 "2026-10-07 17:03:09". */
export function absoluteTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/**
 * 상대 시각을 그리고, 마우스를 올리면 정확한 시각을 보인다.
 *
 * "3분 전" 은 훑기에 좋지만 "어제 몇 시에 꺼졌나" 에는 답하지 못한다 — 예전에는 그 답이 활동 로그에만
 * 있었다. `<time>` 으로 그려 화면 낭독기와 복사에도 정확한 값이 실린다.
 */
export function RelTime({ iso, now }: { iso: string | null; now?: Date }): ReactNode {
  if (!iso) return relativeTime(null);
  return (
    <time dateTime={iso} title={absoluteTime(iso)}>
      {relativeTime(iso, now)}
    </time>
  );
}

/**
 * 상대 시각. "3분 전"이 "2026-06-15T03:00:00Z"보다 운영에 쓸모 있다.
 *
 * **앞선 시각도 다룬다.** 만료 시각(세션·설치 토큰)을 그대로 넣는 자리가 있어서,
 * 미래를 전부 "방금"으로 뭉개면 "3일 뒤 만료"가 "방금 만료"로 보인다.
 * 1분 안쪽의 차이는 양쪽 모두 "방금"으로 둔다 — 시계 오차를 흡수하려는 것이다.
 */
export function relativeTime(iso: string | null, now: Date = new Date()): string {
  if (!iso) return '기록 없음';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '기록 없음';

  const seconds = Math.round((now.getTime() - then) / 1000);
  const suffix = seconds >= 0 ? '전' : '뒤';
  const magnitude = Math.abs(seconds);

  if (magnitude < 60) return seconds >= 0 && magnitude >= 1 ? `${magnitude}초 전` : '방금';
  const minutes = Math.round(magnitude / 60);
  if (minutes < 60) return `${minutes}분 ${suffix}`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}시간 ${suffix}`;
  return `${Math.round(hours / 24)}일 ${suffix}`;
}

export function formatBytes(bytes: number | null): string {
  if (bytes === null || bytes === 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / 1024 ** exponent).toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

export function formatSeconds(seconds: number): string {
  // 먼저 초 단위로 반올림한다 — 영상 길이는 소수(30.03초)라, 나눈 나머지를 반올림하면 119.7초가 "1분 60초" 가 됐다.
  const total = Math.round(seconds);
  if (total < 60) return `${total}초`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return `${minutes}분 ${total % 60}초`;
  return `${Math.floor(minutes / 60)}시간 ${minutes % 60}분`;
}

const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

export function formatDays(days: readonly number[]): string {
  if (days.length === 7) return '매일';
  if (days.length === 5 && [1, 2, 3, 4, 5].every((d) => days.includes(d))) return '평일';
  if (days.length === 2 && days.includes(0) && days.includes(6)) return '주말';
  return days
    .slice()
    .sort((a, b) => a - b)
    .map((day) => DAY_LABELS[day] ?? '?')
    .join('·');
}

/**
 * 시간 구간 표기. 심야 구간은 그 사실이 드러나야 한다 —
 * `22:00 ~ 02:00` 을 그냥 적으면 잘못 입력한 것처럼 보인다.
 */
export function formatDaypart(start: string, end: string): string {
  const overnight = end <= start;
  return overnight ? `${start} ~ ${end} (익일)` : `${start} ~ ${end}`;
}
